// Reusable prev/next controls that drive the nearest carousel within the
// same section — either Shopify's native <slideshow-component> (used by the
// "carousel" section type) or a plain scroll-snap container marked with
// [data-scroll-carousel] (used by hand-built product galleries, which also
// get mouse drag-to-scroll here).
//
// Scoped via [data-carousel-scope], an attribute every section using this
// block must add to its own outer wrapper. This is deliberately NOT based on
// Shopify's auto-generated section wrapper ID/classes: relying on guessed
// platform markup previously caused every instance on a page to bind to
// whichever carousel happened to be first in the DOM. Owning both sides of
// this contract (the attribute and the lookup) guarantees each instance only
// ever controls its own carousel, no matter how many are on the page.
class CarouselNavArrows extends HTMLElement {
  connectedCallback() {
    // Deferred one frame as cheap insurance against any custom-element
    // upgrade/ordering edge case — content_for-rendered sibling blocks are
    // already present in the server-rendered HTML by this point, but this
    // costs nothing and rules the timing question out entirely.
    requestAnimationFrame(() => this.#init());
  }

  #init() {
    const root = this.closest('[data-carousel-scope]') || this.closest('[id^="shopify-section-"]') || document;

    this.prevButton = this.querySelector('[data-arrow="previous"]');
    this.nextButton = this.querySelector('[data-arrow="next"]');

    if (!this.prevButton || !this.nextButton) return;

    this.slideshow = root.querySelector('slideshow-component');
    this.scrollContainer = root.querySelector('[data-scroll-carousel]');

    if (this.slideshow) {
      this.#bindSlideshow();
    } else if (this.scrollContainer) {
      this.#bindScrollContainer();
    }
  }

  #bindSlideshow() {
    this.prevButton.addEventListener('click', () => this.slideshow.previous());
    this.nextButton.addEventListener('click', () => this.slideshow.next());
    this.slideshow.addEventListener('slideshow:select', () => this.#updateSlideshowState());
    this.#updateSlideshowState();
  }

  #updateSlideshowState() {
    if (this.slideshow.infinite) {
      this.prevButton.disabled = false;
      this.nextButton.disabled = false;
      return;
    }

    const slides = this.slideshow.slides || this.slideshow.querySelectorAll('slideshow-slide');
    const current = this.slideshow.current ?? 0;

    // nextIndex accounts for how many slides are visible at once (columns),
    // matching the same accessor the native component uses for its own
    // built-in controls — current >= slides.length - 1 only holds true for
    // single-slide-at-a-time carousels and left multi-column carousels
    // needing one extra, no-op click before disabling.
    this.prevButton.disabled = current <= 0;
    this.nextButton.disabled = this.slideshow.nextIndex >= slides.length;
  }

  #bindScrollContainer() {
    const container = this.scrollContainer;

    const step = () => {
      const firstItem = container.firstElementChild;
      return firstItem ? firstItem.getBoundingClientRect().width + 16 : container.clientWidth * 0.8;
    };

    this.prevButton.addEventListener('click', () => {
      container.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    this.nextButton.addEventListener('click', () => {
      container.scrollBy({ left: step(), behavior: 'smooth' });
    });

    const updateScrollState = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      this.prevButton.disabled = container.scrollLeft <= 1;
      this.nextButton.disabled = maxScroll <= 1 || container.scrollLeft >= maxScroll - 1;
    };

    container.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    // A single measurement taken immediately on load is unreliable: images
    // inside the carousel haven't finished loading yet, so scrollWidth can
    // under-report the true content width, incorrectly disabling "next"
    // permanently. ResizeObserver re-measures every time the container's
    // actual layout size changes (including from images loading in), so the
    // disabled state stays correct regardless of load timing.
    const resizeObserver = new ResizeObserver(() => updateScrollState());
    resizeObserver.observe(container);
    for (const img of container.querySelectorAll('img')) {
      if (!img.complete) img.addEventListener('load', updateScrollState, { once: true });
    }

    updateScrollState();

    CarouselNavArrows.#enableDragToScroll(container);
  }

  /**
   * Mouse drag-to-scroll for a horizontally-scrolling container. Native
   * overflow-x:auto only responds to a scrollbar, touchpad, or touch drag —
   * plain mouse click-and-drag needs this wired up manually.
   */
  static #enableDragToScroll(container) {
    if (container.dataset.dragEnabled) return;
    container.dataset.dragEnabled = 'true';

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;

    container.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return; // native touch scroll already works
      isDown = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = container.scrollLeft;
    });

    container.addEventListener('pointermove', (event) => {
      if (!isDown) return;
      const delta = event.clientX - startX;

      // Only commit to a drag (pointer capture + is-dragging) once the
      // pointer has actually moved past the click-vs-drag threshold. Doing
      // this on pointerdown instead retargets every plain click to the
      // container, which is what made the <a> cards inside stop navigating.
      if (!moved && Math.abs(delta) > 5) {
        moved = true;
        container.setPointerCapture(event.pointerId);
        container.classList.add('is-dragging');
      }

      container.scrollLeft = startScrollLeft - delta;
    });

    const endDrag = (event) => {
      isDown = false;
      if (event && container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
      container.classList.remove('is-dragging');
    };
    container.addEventListener('pointerup', endDrag);
    container.addEventListener('pointerleave', endDrag);
    container.addEventListener('pointercancel', endDrag);

    // Prevent link clicks firing when the pointerdown/up was actually a drag.
    container.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { capture: true }
    );
  }
}

if (!customElements.get('carousel-nav-arrows')) {
  customElements.define('carousel-nav-arrows', CarouselNavArrows);
}
